import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  ListItem,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  List,
  makeStyles,
} from '@material-ui/core';
import { ExpandMore, ChevronRight, DragHandle } from '@material-ui/icons';
import { useSession } from '../../utility/services/customHooks';
import { get } from 'lodash';
import { isProductionEnv, USER_CLASSIFICATION } from '../../utility/common';
import HubSpotService, { FormConfigMap } from '../../utility/services/hubSpot';
import CRInfoModal from '../content-recommendations/CRInfoModal';
import { FormDropDown } from '../../../src/components/formElements';
import { delayTimer } from '../../utility/cr-common';
import { Tooltip } from '@material-ui/core';
import { useRollbar } from '@rollbar/react';
import { OpenInNew } from '@material-ui/icons';
import { segmentEventApi } from '../../constants/analyticsMeta';
import Link from 'next/link';
import { useSelector } from 'react-redux';
import { SUBSCRIPTION_STATUS } from '../../constants/common';
import { isSideNavItemActive, personalFolderIdsFromState } from '../../utility/adhocSideNavActive';
import { useIsInternalUser } from '../../utility/services/customHooks/auth';
import {
  isTaxonomyDesignerExposed,
  TAXONOMY_DESIGNER_SIDEBAR_KEY,
} from '../settings/category/taxonomy/taxonomyExposure';

type DropDownMenuProps = {
  id?: string;
  setAnchor: (event: React.MouseEvent<Element, MouseEvent>, name: string) => void;
  pathname?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  subNavs?: any[]; // Update with the appropriate type for subNavs
  name?: string;
  title?: string;
  icon?: React.ReactNode;
  isSection?: boolean;
  sideBarConfig?: { [key: string]: boolean };
  filterChange?: (event) => void;
  isDrawerOpen?: boolean;
  isDemoUser?: boolean;
  isDisabledMenu?: boolean;
  to?: string;
  toExternal?: string;
  IsPlaneNav?: boolean;
  keepDefaultExpanded?: boolean;
  isHelpCenter?: boolean;
  customerName?: string;
  selectBox?: boolean;
  options?: any;
  userType?: string | undefined;
  defaultUser?: string | undefined;
  isAtBottom?: boolean;
  showNewBadge?: boolean;
};

const DropDownMenu: React.FC<DropDownMenuProps> = ({
  id,
  setAnchor,
  pathname,
  subNavs,
  name,
  title,
  icon,
  isSection,
  sideBarConfig,
  isDrawerOpen,
  isDemoUser,
  isDisabledMenu,
  to,
  toExternal,
  IsPlaneNav,
  keepDefaultExpanded,
  isHelpCenter,
  customerName,
  selectBox,
  filterChange,
  options,
  userType,
  defaultUser,
  isAtBottom = false,
  showNewBadge = false,
}) => {
  const { session } = useSession();
  const isInternalUser = useIsInternalUser();
  const rollbar = useRollbar();
  const personalFolders = useSelector((state: any) => state.root.personalFolders || []);
  const personalFolderId = useSelector((state: any) => state.root.personalFolderId as string | null);
  const personalFolderIds = useMemo(
    () => personalFolderIdsFromState(personalFolders, personalFolderId),
    [personalFolders, personalFolderId]
  );
  const expandedInitially = keepDefaultExpanded && isDisabledMenu;
  const [expanded, handleDropDown] = useState(expandedInitially);
  const talkToSaleText = `Upgrade to an ${
    userType === USER_CLASSIFICATION.CONTENT_AI
      ? 'Content AI'
      : userType === USER_CLASSIFICATION.QWA_TOOLS
      ? 'AI Tools'
      : 'AI SEO Suite'
  } package to access {}. Talk to sales to see {} in action and to learn about other ${
    userType === USER_CLASSIFICATION.CONTENT_AI
      ? 'Content AI'
      : userType === USER_CLASSIFICATION.QWA_TOOLS
      ? 'AI Tools'
      : 'AI SEO Suite'
  } benefits and packages`;
  const [talkSalesContent, setTalkSalesContent] = useState(talkToSaleText);
  const [showLockIcon, setShowLockIcon] = useState(false);
  const [isAIVisibilityDashboard, setIsAIVisibilityDashboard] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showModalScheduled, setShowModalScheduled] = useState<boolean>(false);
  const sideName = name !== undefined ? name : '';
  // Use the isDrawerOpen prop instead of Redux state for better synchronization
  const sidebarOpen = isDrawerOpen;

  const useStyles = makeStyles((theme) => ({
    sectionClass: {
      width: '240px',
      height: '18px',
      fontFamily: 'IBM Plex Sans !important',
      fontStyle: 'normal',
      fontWeight: 500,
      fontSize: '14px',
      lineHeight: '18px',
      letterSpacing: '0.2px',
      color: 'rgba(0, 0, 0, 0.6)',
      flex: 'none',
      order: 1,
      flexGrow: 1,
    },
    disabledMenu: {
      opacity: 1 /* Optionally, reduce the opacity to visually indicate it is disabled */,
    },
    openInNew: {
      width: '27px',
      height: '16px',
    },
    anchorClass: {
      textDecoration: 'none',
      color: 'inherit',
    },
    formControl: {
      width: '100%',
      border: '1px solid #ced4da', // border color
      borderRadius: '4px', // border radius
      '&:focus-within': {
        borderColor: theme.palette.primary.main, // focus border color
      },
    },
  }));
  const classes = useStyles();

  // Function to check if pathname matches the sideName correctly
  // This prevents false positives like 'settings' matching 'intent-settings'
  const checkPathMatch = (pathname: string, sideName: string) => {
    // For settings specifically, ensure it's in the correct path segment
    if (sideName === 'settings') {
      return pathname.includes('/app/settings/') || pathname.endsWith('/app/settings');
    } else if (sideName === 'research-keywords') {
      return pathname?.includes(sideName) || pathname?.includes('keywords-list');
    } else if (sideName === 'content-recommendations') {
      return pathname?.includes(sideName) && !pathname?.includes('content-brief');
    }
    // For other menu items, use the original logic
    return pathname.includes(sideName);
  };

  useEffect(() => {
    // Check if the current path should expand this menu
    const shouldExpand = pathname && sideName && checkPathMatch(pathname, sideName);
    if (!expanded && shouldExpand) {
      handleDropDown(true);
    }
  }, []);

  useEffect(() => {
    if (isDisabledMenu !== undefined && !isDisabledMenu) {
      setShowLockIcon(true);
    }
  }, [isDisabledMenu]);

  useEffect(() => {
    if (sidebarOpen) {
      const shouldExpand = pathname && sideName && checkPathMatch(pathname, sideName);
      if (shouldExpand) {
        handleDropDown(true);
      }
    } else {
      handleDropDown(false);
    }
  }, [sideName, pathname, sidebarOpen]);

  useEffect(() => {
    // Check if the variables are properly initialized and evaluate to true
    if (keepDefaultExpanded && isDisabledMenu && sidebarOpen) {
      // Set the state to true
      handleDropDown(true);
    }
  }, [keepDefaultExpanded, isDisabledMenu, sidebarOpen]);

  const onNavClick = useCallback((path: string) => {
    if (path) {
      if (window.analytics) {
        window.analytics.track('Menu Option Click', {
          path: path,
        });
      } else {
        segmentEventApi(
          'Menu Option Click',
          {
            path: path,
          },
          'track'
        );
      }
    }
  }, []);

  const analyticsEvent = (value) => {
    if (window.analytics) {
      window.analytics.track(`Switched to ${value} Dashboard`);
    } else {
      segmentEventApi(
        `Switched to ${value} Dashboard`,
        {
          option: value,
        },
        'track'
      );
    }
  };
  const handleClick = useCallback(
    (title, uniqueKey?: string) => {
      const isAIVisibility = uniqueKey === 'ai-visibility-dashboard' || title === 'AI Visibility Dashboard';
      setIsAIVisibilityDashboard(isAIVisibility);
      setTalkSalesContent(talkToSaleText.replace(/{}/g, title));
      if (session?.user?.isContentAiSignUp === true && defaultUser === USER_CLASSIFICATION.QWA_TOOLS) {
        setShowModal(false);
      } else {
        setShowModal(true);
      }
    },
    [setShowModal]
  );

  const handleSubmitTalktoSale = useCallback(async () => {
    setLoading(true);
    setShowLoader(true);
    await delayTimer(2000);
    setLoading(false);
    setShowLoader(false);
    setShowModal(false);
    const email = get(session, 'user.email');
    const userName = get(session, 'user.name', '');
    const firstName = userName ? userName.split(' ')[0] : '';
    const lastName = userName ? userName.split(' ')[1] : '';
    const formValues = {
      firstname: firstName,
      lastname: lastName,
      email,
      company: customerName,
      Page_Source: 'Content AI - Lock Icon',
    };
    if (isProductionEnv) {
      HubSpotService.sendDataToForm(FormConfigMap.TalkToSalesForm, { ...formValues }, undefined).catch((error) => {
        rollbar.warning('Error sending data to HubSpot in the Request a Demo form', error);
      });
    }
    setShowModalScheduled(true);
  }, []);

  const handleToolUserTalkToSale = () => {
    window.open('https://calendly.com/anuragsinghal/eu');
  };

  const handleAIVisibilityIntercom = useCallback(() => {
    if (window && (window as any).Intercom) {
      (window as any).Intercom(
        'showNewMessage',
        `I would like to setup AI visibility dashboard for ${customerName || 'my brand'}.`
      );
    }
    setShowModal(false);
  }, [customerName]);
  const handleSubmitScheduledMetting = useCallback(() => {
    window.open(process.env.NEXT_PUBLIC_CALENDLY_URL_CONTENT_AI);
    setShowModal(false);
    setShowModalScheduled(false);
  }, [setShowModalScheduled]);

  const cancelHandler = useCallback(() => {
    setShowModal(false);
  }, [setShowModal]);

  const cancelHandlerScheduledMeeting = useCallback(() => {
    setShowModalScheduled(false);
  }, [setShowModalScheduled]);

  const isSubMenuLinkVisible = (key: string) => {
    if (sideBarConfig !== undefined) {
      if (userType === 'QWA_TOOLS') {
        return true;
      }
      // Special handling for settings-setup and settings-team-members - allow access if it has display: true
      if ((key === 'settings-setup' || key === 'settings-team-members') && name === 'settings') {
        return true;
      }
      // The designer's own rule (taxonomyExposure): internal users on every
      // workspace, customers where it is switched on. Internal users used to
      // reach it through a Manage Categories tab, which is gone.
      if (key === TAXONOMY_DESIGNER_SIDEBAR_KEY) {
        return isTaxonomyDesignerExposed({ isInternalUser: isInternalUser === true, sidebarLinkConfig: sideBarConfig });
      }
      return sideBarConfig[key] || false;
    }
  };

  const isMenuActive = useMemo(() => {
    if (sideName === 'content-recommendations') {
      return pathname?.includes(sideName) && !pathname?.includes('content-brief');
    } else if (sideName === 'research-keywords') {
      return pathname?.includes(sideName) || pathname?.includes('keywords-list');
    }
    return pathname && sideName && checkPathMatch(pathname, sideName);
  }, [pathname, sideName]);

  /**
   * Whether to draw the expand/collapse chevron.
   *
   * It used to be driven by `isDisabledMenu` alone, which is an ENTITLEMENT flag
   * (`sidebar_link_config[uniqueKey]`), not a statement about whether the nav
   * opens. An internal-only nav such as Admin is in no customer's config, so the
   * flag comes back `undefined` and the chevron never rendered — even though the
   * nav expands on click exactly like Settings beside it.
   *
   * The added clause covers only that case: no config entry AND real sub-items.
   * A nav explicitly switched OFF (`false`) is untouched, so a locked nav still
   * shows its lock and no chevron, and an enabled nav behaves exactly as before.
   */
  const showExpandChevron = Boolean(isDisabledMenu || (isDisabledMenu === undefined && subNavs?.length));

  return (
    <div id={id}>
      <ListItem
        button
        className={isMenuActive && IsPlaneNav ? 'list-item active' : expanded || isMenuActive ? 'active' : 'active-1'}
        onClick={() => {
          if (subNavs) {
            handleDropDown(!expanded);
          }
        }}
      >
        {!isSection && !selectBox && (
          <ListItemIcon
            className={!isDisabledMenu && !isHelpCenter ? `${classes.disabledMenu} sidebar-icon` : 'sidebar-icon'}
            onMouseOver={(e: React.MouseEvent<Element, MouseEvent>) => {
              if (!sidebarOpen) {
                setAnchor(e, sideName);
              }
            }}
          >
            {icon}
          </ListItemIcon>
        )}
        {selectBox ? (
          <FormDropDown
            id="opportunities__fromdropdown__opportunity_explorer_27"
            defaultValue={userType || USER_CLASSIFICATION.AI_SEO_SUITE}
            onChange={(e) => {
              filterChange?.(e.target.value);
              analyticsEvent(e.target.value);
            }}
            options={options}
            className="simple-dropdown w-100"
            style={{ fontSize: '14px', height: '30px', background: '#ffffff', top: '0px' }}
            fullWidth
            downArrowChange={'down-arrow-change'}
            classes={classes}
            isAtBottom={isAtBottom}
          />
        ) : null}
        {!isSection ? (
          <>
            {to || toExternal ? (
              <>
                {to &&
                  // Locked top-level item: don't render Link (so navigation can't fire);
                  // render a click-to-modal div instead. Mirrors the subnav lock pattern.
                  (isDisabledMenu === false ? (
                    <div
                      style={{ flex: '1 1 auto', cursor: 'pointer' }}
                      onClick={(e) => {
                        e.preventDefault();
                        handleClick(title, sideName);
                      }}
                    >
                      <ListItemText
                        primary={
                          showNewBadge ? (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                              {title}
                              <span className="new-badge">NEW</span>
                            </span>
                          ) : (
                            title
                          )
                        }
                        className={classes.disabledMenu}
                      />
                    </div>
                  ) : (
                    // A real <a>, like the sub-items: right click shows the browser's link menu and
                    // Cmd/Ctrl + click or middle click opens a new tab; a plain click still navigates
                    // in the app. It takes ListItemText's flex slot, so the row looks the same, and
                    // tabIndex -1 keeps the Tab order as before.
                    <Link
                      key={to}
                      href={to}
                      className={classes.anchorClass}
                      style={{ flex: '1 1 auto', minWidth: 0 }}
                      tabIndex={-1}
                    >
                      <ListItemText
                        primary={
                          showNewBadge ? (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                              {title}
                              <span className="new-badge">NEW</span>
                            </span>
                          ) : (
                            title
                          )
                        }
                        className={''}
                      />
                    </Link>
                  ))}
                {toExternal ? (
                  isDisabledMenu === false ? (
                    <div
                      style={{ flex: '1 1 auto' }}
                      onClick={() => {
                        handleClick(title);
                      }}
                    >
                      <ListItemText primary={title} className={''} />
                    </div>
                  ) : (
                    <div style={{ flex: '1 1 auto' }}>
                      <a
                        target="_blank"
                        key={toExternal}
                        href={toExternal}
                        className={classes.anchorClass}
                        rel="noreferrer"
                      >
                        <ListItemText primary={title} className={''} />
                      </a>
                    </div>
                  )
                ) : null}
              </>
            ) : (
              <ListItemText primary={title} className={!isDisabledMenu && !isHelpCenter ? classes.disabledMenu : ''} />
            )}
            {showLockIcon && !isDisabledMenu ? (
              <Tooltip
                title={
                  defaultUser === USER_CLASSIFICATION.QWA_TOOLS && session?.user?.isContentAiSignUp
                    ? 'Complete onboarding to access this feature'
                    : `This feature is not included in your ${
                        defaultUser === USER_CLASSIFICATION.QWA_TOOLS ? 'AI Tools' : 'Content AI'
                      } plan`
                }
              >
                <svg
                  onClick={() => {
                    handleClick(title);
                  }}
                  style={{ marginTop: '6px' }}
                  className={`MuiSvgIcon-root MuiSvgIcon-fontSizeMedium MuiBox-root css-1om0hkc ${classes.disabledMenu}`}
                  focusable="true"
                  aria-hidden="true"
                  viewBox="0 0 33 33"
                  data-testid="ContentPasteSearchOutlinedIcon"
                >
                  <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM9 6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9V6zm9 14H6V10h12v10zm-6-3c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2z"></path>
                </svg>
              </Tooltip>
            ) : null}
          </>
        ) : (
          <div className={'sidebar-list-close'}>
            <span className={classes.sectionClass}>
              {isDrawerOpen ? title : <DragHandle style={{ marginLeft: '-2px', fontSize: '23px' }} />}
            </span>
          </div>
        )}
        {!IsPlaneNav && (
          <>
            {expanded ? (
              !isSection && showExpandChevron && !selectBox ? (
                <ExpandMore className={''} />
              ) : isHelpCenter && isDisabledMenu !== false ? (
                <a
                  target="_blank"
                  key={toExternal}
                  href={toExternal}
                  className={classes.anchorClass}
                  style={{ marginTop: '6px' }}
                  rel="noreferrer"
                >
                  <OpenInNew className={classes.openInNew} />
                </a>
              ) : null
            ) : !isSection && showExpandChevron && !selectBox ? (
              <ChevronRight className={''} />
            ) : isHelpCenter && isDisabledMenu !== false ? (
              <a
                target="_blank"
                key={toExternal}
                href={toExternal}
                className={classes.anchorClass}
                style={{ marginTop: '6px' }}
                rel="noreferrer"
              >
                <OpenInNew className={classes.openInNew} />
              </a>
            ) : !isSection && !customerName && !selectBox ? (
              <ChevronRight className={''} />
            ) : null}
          </>
        )}
      </ListItem>
      {subNavs && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {subNavs
              .filter((i) => !i.hidden)
              .filter((item, index, subNavs) => subNavs.indexOf(item) === index)
              .map((item) => {
                const isInternal = item?.isInternal;

                let isVisible = true;
                if (!isDemoUser && (defaultUser === USER_CLASSIFICATION.QWA_TOOLS || !isInternal)) {
                  isVisible = Boolean(isSubMenuLinkVisible(item?.uniqueKey));
                }
                let showLockPopupForExpiredContentAiUSer = true;

                if (
                  defaultUser === USER_CLASSIFICATION.CONTENT_AI &&
                  session?.user.subscription_status === SUBSCRIPTION_STATUS.SUBSCRIPTION_CANCELLED
                ) {
                  // console.log(item?.uniqueKey, isSubMenuLinkVisible(item?.uniqueKey))
                  showLockPopupForExpiredContentAiUSer = Boolean(isSubMenuLinkVisible(item?.uniqueKey));
                }

                /**
                 * @type {string}
                 */
                const title = item?.title;
                const formattedTitle = title?.toLocaleLowerCase().replace(' ', '_');
                return (
                  <Link key={item.to} href={item.to} passHref legacyBehavior>
                    <ListItem
                      component="a"
                      id={`layout__menu__side_bar__${formattedTitle}`}
                      button
                      onClick={(e) => {
                        if (
                          (!isVisible &&
                            (customerName || showLockIcon) &&
                            (defaultUser === USER_CLASSIFICATION.QWA_TOOLS || !isInternal)) ||
                          !showLockPopupForExpiredContentAiUSer
                        ) {
                          e.preventDefault();
                          const isAIVisibility =
                            item?.uniqueKey === 'ai-visibility-dashboard' || item.title === 'AI Visibility Dashboard';
                          setIsAIVisibilityDashboard(isAIVisibility);
                          setTalkSalesContent(talkToSaleText.replace(/{}/g, item.title));
                          if (
                            session?.user?.isContentAiSignUp === true &&
                            defaultUser === USER_CLASSIFICATION.QWA_TOOLS
                          ) {
                            setShowModal(false);
                          } else {
                            setShowModal(true);
                          }
                        } else {
                          onNavClick(item.to);
                        }
                      }}
                      className={`list-item ${isSideNavItemActive(item, pathname, personalFolderIds) ? 'active' : ''}`}
                      //disabled={!isVisible}
                    >
                      <ListItemIcon>{item?.showNewBadge ? <span className="new-badge">NEW</span> : null}</ListItemIcon>
                      {(showLockIcon && !isDisabledMenu) || (!isVisible && customerName) ? (
                        <Tooltip
                          title={
                            defaultUser === USER_CLASSIFICATION.QWA_TOOLS && session?.user?.isContentAiSignUp
                              ? 'Complete onboarding to access this feature'
                              : `This feature is not included in your ${
                                  defaultUser === USER_CLASSIFICATION.QWA_TOOLS ? 'AI Tools' : 'Content AI'
                                } plan`
                          }
                        >
                          <svg
                            className={`MuiSvgIcon-root MuiSvgIcon-fontSizeMedium MuiBox-root css-1om0hkc ${classes.disabledMenu}`}
                            style={{ marginTop: '5px', marginLeft: '0' }}
                            focusable="false"
                            aria-hidden="true"
                            viewBox="0 0 36 36"
                            data-testid="ContentPasteSearchOutlinedIcon"
                          >
                            <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM9 6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9V6zm9 14H6V10h12v10zm-6-3c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2z"></path>
                          </svg>
                        </Tooltip>
                      ) : null}
                      <ListItemText primary={item.title} />
                    </ListItem>
                  </Link>
                );
              })}
          </List>
        </Collapse>
      )}
      {showModal ? (
        <CRInfoModal
          open={true}
          content={
            isAIVisibilityDashboard
              ? 'Track how your brand appears in AI-powered search results. Talk to us to get started with AI Visibility Dashboard.'
              : talkSalesContent
          }
          header={isAIVisibilityDashboard ? 'Start tracking AI visibility' : 'Upgrade now to unlock this feature!'}
          handleClose={cancelHandler}
          title={'un'}
          cancelName={'Close'}
          buttontext={isAIVisibilityDashboard ? 'Talk to Us' : 'Talk to Sales'}
          handleSubmit={
            isAIVisibilityDashboard
              ? handleAIVisibilityIntercom
              : defaultUser !== USER_CLASSIFICATION.QWA_TOOLS
              ? handleSubmitTalktoSale
              : handleToolUserTalkToSale
          }
          modalWidth={500}
          isloading={loading || false}
          isLoaderShow={showLoader || false}
        />
      ) : null}

      {showModalScheduled ? (
        <CRInfoModal
          open={true}
          content={
            'Noted! Someone from our sales team will contact you shortly. You may also schedule a time using the button below.'
          }
          header={''}
          handleClose={cancelHandlerScheduledMeeting}
          title={'un'}
          cancelName={'Close'}
          buttontext={'Schedule a meeting'}
          handleSubmit={handleSubmitScheduledMetting}
          modalWidth={500}
        />
      ) : null}
    </div>
  );
};

export default DropDownMenu;
